import { useCallback, useEffect, useRef } from "react";
import {
  ArrowUpload24Regular,
  Dismiss16Regular,
  DocumentMultipleRegular,
  Search24Regular,
  WarningRegular,
} from "@fluentui/react-icons";
import { useDocuparserDocuments } from "../hooks/useDocuparserDocuments";
import { DocumentCard } from "./DocumentCard";
import styles from "./DocumentListView.module.css";

interface DocumentListViewProps {
  readonly onUpload: () => void;
  readonly onOpenDocument: (docId: number) => void;
}

export function DocumentListView({ onUpload, onOpenDocument }: Readonly<DocumentListViewProps>) {
  const {
    searchTerm,
    setSearchTerm,
    activeSearchTerm,
    search,
    clearSearch,
    documents,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
    refresh: loadDocuments,
  } = useDocuparserDocuments();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Infinite scroll: observe a sentinel element at the bottom of the list —
  // once it's visible inside the scrollable container, fetch the next page
  // and append it. `root` must be the scroll container itself (not the
  // default viewport) since the list scrolls inside a fixed-height panel.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root, rootMargin: "80px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      search(searchTerm);
      if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
    },
    [search, searchTerm],
  );

  const handleClearSearch = useCallback(() => {
    clearSearch();
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [clearSearch]);

  function renderDocumentsBody() {
    if (isLoading) {
      return (
        <div className={styles.loadingState}>
          <output className={styles.spinnerLarge} aria-label="Loading documents" />
          <p className={styles.loadingText}>Loading documents...</p>
        </div>
      );
    }

    if (documents.length === 0) {
      return renderEmptyState();
    }

    return (
      <>
        <ul className={styles.documentsList}>
          {documents.map((doc) => (
            <DocumentCard key={doc.id} document={doc} onOpen={onOpenDocument} />
          ))}
        </ul>

        {/* Sentinel observed by IntersectionObserver — becoming visible
            triggers loadMore(). Renders even once hasMore is false so the
            ref stays stable; loadMore() itself is a no-op in that case. */}
        <div ref={sentinelRef} aria-hidden="true" />

        {isLoadingMore && (
          <div className={styles.loadingMore}>
            <output className={styles.spinnerSmall} aria-label="Loading more documents" />
          </div>
        )}

        {error && (
          <div className={styles.loadMoreError}>
            <p className={styles.loadMoreErrorText}>{error.message}</p>
            <button type="button" className={styles.btn} onClick={loadMore} aria-label="Retry loading more documents">
              Retry
            </button>
          </div>
        )}

        {!hasMore && !isLoadingMore && !error && (
          <p className={styles.endOfList}>You&apos;ve reached the end of the list.</p>
        )}
      </>
    );
  }

  function renderEmptyState() {
    if (error) {
      return (
        <div className={styles.emptyState}>
          <WarningRegular className={styles.emptyIconError} aria-hidden="true" />
          <h3 className={styles.emptyTitle}>Error</h3>
          <p className={styles.emptyMessage}>{error.message}</p>
          <button type="button" className={styles.btn} onClick={loadDocuments} aria-label="Retry loading documents">
            Retry
          </button>
        </div>
      );
    }

    if (activeSearchTerm) {
      return (
        <div className={styles.emptyState}>
          <DocumentMultipleRegular className={styles.emptyIcon} aria-hidden="true" />
          <h3 className={styles.emptyTitle}>No documents found</h3>
          <p className={styles.emptyMessage}>No results for &quot;{activeSearchTerm}&quot;. Try a different search.</p>
          <button type="button" className={styles.btn} onClick={handleClearSearch} aria-label="Clear search">
            Clear Search
          </button>
        </div>
      );
    }

    return (
      <div className={styles.emptyState}>
        <DocumentMultipleRegular className={styles.emptyIcon} aria-hidden="true" />
        <h3 className={styles.emptyTitle}>No documents found</h3>
        <p className={styles.emptyMessage}>No documents available at the moment.</p>
      </div>
    );
  }

  return (
    <div className={styles.listPage}>
      <div className={styles.searchSection}>
        <form className={styles.searchControls} onSubmit={handleSearchSubmit}>
          <div className={styles.searchInputWrapper}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by name, status ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search documents"
            />
            {searchTerm && (
              <button type="button" className={styles.clearBtn} onClick={handleClearSearch} aria-label="Clear search">
                <Dismiss16Regular aria-hidden="true" />
              </button>
            )}
            <button type="submit" className={styles.searchBtn} aria-label="Search" disabled={isLoading}>
              <Search24Regular aria-hidden="true" />
            </button>
          </div>
          <button type="button" className={styles.uploadBtn} onClick={onUpload} aria-label="Upload document">
            <ArrowUpload24Regular aria-hidden="true" />
          </button>
        </form>
      </div>

      <div className={styles.documentsWrapper} ref={scrollContainerRef}>
        {renderDocumentsBody()}
      </div>
    </div>
  );
}
